import React, { useCallback, useEffect } from 'react';
import { View, Text, StyleSheet } from 'react-native';

import { AttachmentListItem } from './AttachmentListItem';
import { ATTACHMENT_COLORS } from '../../constants/attachmentColors';
import {
  formatAttachmentSectionPathDisplay,
  stripStageNumber,
} from '../../constants/sectionIds';
import { useAttachmentCarousel } from '../../hooks/useAttachmentCarousel';
import { SavedAttachment } from '../../services/attachmentService';
import { type AttachmentGroupedListProps } from '../../types/attachmentInterfaces';
import { AttachmentCarouselModal } from '../modals/AttachmentCarouselModal';

/**
 * AttachmentGroupedList component
 * Displays attachments grouped by stage and substage with headers.
 * Stage headers: plain text (no background), dark color, with attachment count badge and a bottom border divider (on every stage, including the last).
 * Substage headers: plain text (no background), medium-dark color, no underline, no badge.
 * Shows table headers within each substage on desktop, card layout on mobile.
 */
export const AttachmentGroupedList: React.FC<AttachmentGroupedListProps> = ({
  groupedAttachments,
  isEditing,
  isMobile,
  onCaptionChange,
  onDelete,
  pendingCaptionEdits,
  offlineMode = false,
}) => {
  const { carouselProps, openCarousel } = useAttachmentCarousel();

  const handleOpenCarousel = useCallback(
    (
      tapped: SavedAttachment,
      substageAttachments: SavedAttachment[],
      substageLabel: string,
    ) => {
      openCarousel(tapped, substageAttachments, substageLabel);
    },
    [openCarousel],
  );

  useEffect(() => {
    if (
      typeof __DEV__ !== 'undefined' &&
      __DEV__ &&
      typeof window !== 'undefined'
    ) {
      const flatItems = groupedAttachments.flatMap(g =>
        (g.substages ?? []).flatMap(s => s.attachments ?? []),
      );
      console.warn('[attachment-view] AttachmentGroupedList.render', {
        groupCount: groupedAttachments.length,
        stageLabels: groupedAttachments.map(g => g.stageLabel),
        attachmentCount: flatItems.length,
        attachmentIds: flatItems.map(a => a.id),
        source: groupedAttachments,
      });
    }
  }, [groupedAttachments]);

  if (groupedAttachments.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText} testID="attachment-empty-message">
          No attachments
        </Text>
      </View>
    );
  }

  const layout = isMobile ? 'card' : 'table';

  return (
    <View style={styles.container} testID="attachment-list">
      {groupedAttachments.map(stageGroup => {
        const substages = stageGroup.substages ?? [];
        const totalAttachments = substages.reduce(
          (sum, substage) => sum + (substage.attachments ?? []).length,
          0,
        );

        return (
          <View key={stageGroup.stage} style={styles.stageContainer}>
            {/* Stage header — no background, near-black text, count badge, bottom divider */}
            <View style={styles.stageHeader}>
              <Text style={styles.stageHeaderText}>
                {stripStageNumber(stageGroup.stageLabel)}
              </Text>
              <View style={styles.countBadge}>
                <Text style={styles.countBadgeText}>{totalAttachments}</Text>
              </View>
            </View>

            {/* Substages */}
            {substages.map(substage => (
              <View
                key={substage.sectionId || `${stageGroup.stage}-attachments`}
                style={styles.substageContainer}
              >
                {(substage.sectionLabel ?? '').trim().length > 0 ? (
                  <View style={styles.substageHeader}>
                    <Text style={styles.substageHeaderText}>
                      {substage.sectionLabel}
                    </Text>
                  </View>
                ) : null}

                {/* Desktop table headers */}
                {!isMobile && (
                  <View style={styles.tableHeader}>
                    <View style={styles.nameHeaderColumn}>
                      <Text style={styles.headerText}>Name</Text>
                    </View>
                    <View style={styles.dateHeaderColumn}>
                      <Text style={styles.headerText}>Date</Text>
                    </View>
                    <View style={styles.sizeHeaderColumn}>
                      <Text style={styles.headerText}>Size</Text>
                    </View>
                    <View style={styles.actionsHeaderColumn}>
                      <Text style={styles.headerText}>Actions</Text>
                    </View>
                  </View>
                )}

                {/* Attachments */}
                <View>
                  {(substage.attachments ?? []).map(attachment => {
                    const sectionPathLabel = formatAttachmentSectionPathDisplay(
                      attachment.requestSection || substage.sectionId,
                    );
                    return (
                      <AttachmentListItem
                        key={attachment.id}
                        attachment={attachment}
                        layout={layout}
                        isEditing={isEditing}
                        isMobile={isMobile}
                        onCaptionChange={onCaptionChange}
                        onDelete={onDelete}
                        pendingCaptionEdits={pendingCaptionEdits}
                        offlineMode={offlineMode}
                        onOpenCarousel={a =>
                          handleOpenCarousel(
                            a,
                            substage.attachments ?? [],
                            sectionPathLabel,
                          )
                        }
                      />
                    );
                  })}
                </View>
              </View>
            ))}
          </View>
        );
      })}

      {/* Offline indicator */}
      {offlineMode && (
        <View style={styles.offlineIndicator}>
          <Text style={styles.offlineText}>
            Offline Mode - Some actions may be queued
          </Text>
        </View>
      )}

      <AttachmentCarouselModal {...carouselProps} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  emptyContainer: {
    padding: 24,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: ATTACHMENT_COLORS.METADATA_COLOR,
    fontStyle: 'italic',
  },
  stageContainer: {
    marginBottom: 16,
  },
  stageHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 4,
    marginBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: ATTACHMENT_COLORS.STAGE_HEADER_BORDER_COLOR,
  },
  stageHeaderText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: ATTACHMENT_COLORS.STAGE_HEADER_TEXT_COLOR,
    flex: 1,
  },
  substageContainer: {
    marginBottom: 12,
  },
  substageHeader: {
    paddingVertical: 6,
    paddingHorizontal: 4,
    marginBottom: 4,
  },
  substageHeaderText: {
    fontSize: 14,
    fontWeight: '600',
    color: ATTACHMENT_COLORS.SUBSTAGE_HEADER_TEXT_COLOR,
  },
  countBadge: {
    minWidth: 22,
    height: 22,
    borderRadius: 11,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 6,
    backgroundColor: ATTACHMENT_COLORS.SECTION_BADGE_COLOR,
  },
  countBadgeText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#fff',
  },
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ATTACHMENT_COLORS.TABLE_HEADER_BACKGROUND_COLOR,
    borderBottomWidth: 2,
    borderBottomColor: ATTACHMENT_COLORS.CARD_BORDER_COLOR,
    paddingVertical: 8,
    paddingHorizontal: 8,
  },
  nameHeaderColumn: {
    flex: 2,
    paddingRight: 12,
  },
  dateHeaderColumn: {
    width: 120,
    paddingHorizontal: 8,
  },
  sizeHeaderColumn: {
    width: 100,
    paddingHorizontal: 8,
  },
  actionsHeaderColumn: {
    width: 120,
    paddingHorizontal: 8,
  },
  headerText: {
    fontSize: 14,
    fontWeight: '600',
    color: ATTACHMENT_COLORS.FILENAME_COLOR,
  },
  offlineIndicator: {
    backgroundColor: ATTACHMENT_COLORS.OFFLINE_BADGE_COLOR,
    padding: 8,
    alignItems: 'center',
  },
  offlineText: {
    fontSize: 12,
    color: '#fff',
    fontWeight: '500',
  },
});
